import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Handshake, Target, Users, Building2, Calendar, CheckCircle2, ArrowRight, Mail, Phone, MapPin, Sparkles, TrendingUp, Award, Globe, BarChart3 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const ZentharaSponsorshipDetail = () => {
  const offerings = [
    {
      title: 'Sponsorship Strategy & Design',
      description: 'Understanding the event concept, audience profile, and commercial objectives.',
      features: [
        'Identifying sponsorship-ready assets (branding, stage presence, zones, digital touchpoints)',
        'Designing structured sponsorship packages (Title, Co-presenting, Powered-By, Category Partner, Associate Partner, In-kind Partner)'
      ]
    },
    {
      title: 'Sponsor Identification & Outreach',
      description: 'Mapping potential sponsors based on category, audience alignment, budgets, and brand fit.',
      features: [
        'Reaching out through our network of brand, retail, corporate, and agency relationships',
        'Presenting curated opportunities that match both sponsor and organizer expectations'
      ]
    },
    {
      title: 'Negotiation & Closure',
      description: 'Facilitating commercial discussions between organizers and sponsors.',
      features: [
        'Aligning on rights, deliverables, timelines, and expectations from both sides',
        'Supporting documentation through clear proposals, letters of intent, and agreements'
      ]
    },
    {
      title: 'Activation Planning & Integration',
      description: 'Translating the sponsorship agreement into on-ground and in-mall visibility.',
      features: [
        'Recommending sponsor integration across events – branding, kiosks, experience zones, sampling, contests, digital integration',
        'Ensuring sponsor presence enhances the event experience'
      ]
    },
    {
      title: 'Post-Event Review & Relationship Continuity',
      description: 'Assisting with post-event review inputs and encouraging long-term partnerships.',
      features: [
        'Basic post-event review inputs (where feasible)',
        'Long-term partnerships across multiple seasons, formats, and locations instead of "one-off" sponsorships'
      ]
    }
  ];

  const stakeholders = [
    {
      icon: <Building2 size={40} />,
      title: 'Malls & Retail Developments',
      description: 'Planning events, festivals, campaigns, and community initiatives'
    },
    {
      icon: <Users size={40} />,
      title: 'Corporate Organizations',
      description: 'Hosting conferences, launches, CSR events, employee engagements, and customer programs'
    },
    {
      icon: <Calendar size={40} />,
      title: 'Educational Institutions & IP Owners',
      description: 'Conducting festivals, exhibitions, and niche events'
    },
    {
      icon: <Target size={40} />,
      title: 'Brands & Advertisers',
      description: 'Looking for targeted, on-ground, in-mall, or community-based sponsorship opportunities'
    }
  ];

  const advantages = [
    {
      icon: <Globe size={40} />,
      title: 'Deep Retail Understanding',
      description: 'Deep understanding of retail audiences and catchments across multiple cities'
    },
    {
      icon: <Handshake size={40} />,
      title: 'Direct Access',
      description: 'Direct access to malls, developers, and brand decision-makers'
    },
    {
      icon: <TrendingUp size={40} />,
      title: 'Integrated Strategy',
      description: 'Ability to integrate sponsorships into larger leasing, activation, and brand expansion strategies'
    },
    {
      icon: <Award size={40} />,
      title: 'Partnership Focus',
      description: 'Solutions-oriented mindset focused on long-term partnerships rather than isolated deals'
    }
  ];

  return (
    <div style={{ background: 'var(--bg-primary)', minHeight: '100vh' }}>
      <Header />
      <main style={{ paddingTop: '180px' }}>
        {/* Hero Section */}
        <section style={{
          background: 'linear-gradient(135deg, #001a3d 0%, #002b5c 50%, #003d7a 100%)',
          padding: '100px 7.6923%',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Background Pattern */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url(https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=1920)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.15
          }} />

          <div style={{ maxWidth: '1400px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
            <Link to="/zenthara" style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              color: '#ff6b00',
              textDecoration: 'none',
              fontSize: '16px',
              marginBottom: '30px',
              transition: 'all 0.3s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateX(-5px)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateX(0)'}
            >
              <ArrowLeft size={20} />
              Back to Zenthara
            </Link>

            <div style={{ maxWidth: '900px' }}>
              {/* Zenthara Logo */}
              <img 
                src="https://customer-assets.emergentagent.com/job_biz-innovations/artifacts/m9542jkn_Zenthara%20Final%20Logo.jpeg" 
                alt="Zenthara" 
                style={{ height: '80px', objectFit: 'contain', marginBottom: '20px' }}
              />
              <p style={{ 
                color: '#ff6b00', 
                fontSize: '14px', 
                fontWeight: '600', 
                letterSpacing: '2px',
                marginBottom: '20px'
              }}>
                A NEW VERTICAL
              </p>
              <h1 style={{ 
                fontSize: '48px', 
                fontWeight: '700', 
                color: '#ffffff',
                marginBottom: '24px',
                lineHeight: '1.2'
              }}>
                Zenthara <span style={{ color: '#ff6b00' }}>Sponsorship & Brand Alliances</span>
              </h1>
              <p style={{ 
                fontSize: '20px', 
                color: 'rgba(255,255,255,0.9)',
                marginBottom: '40px',
                lineHeight: '1.8'
              }}>
                Connecting organizations and event IPs with the right brands and sponsors. We design and manage strategic sponsorships that deliver visibility, engagement, and measurable outcomes for all stakeholders in the retail ecosystem.
              </p>
              <a href="#contact" style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '10px',
                padding: '16px 32px',
                background: '#ff6b00',
                color: '#ffffff',
                textDecoration: 'none',
                fontWeight: '600',
                fontSize: '16px',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.3s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#e55a00'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#ff6b00'}
              >
                Connect With Us
                <ArrowRight size={20} />
              </a>
            </div>
          </div>
        </section>

        {/* Introduction Section */}
        <section style={{ background: 'var(--bg-primary)', padding: '100px 7.6923%' }}>
          <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
            <div style={{
              display: 'inline-block',
              padding: '8px 16px',
              background: 'rgba(255, 107, 0, 0.1)',
              border: '1px solid #ff6b00',
              marginBottom: '20px',
              fontSize: '14px',
              color: '#ff6b00',
              fontWeight: '500'
            }}>
              INTRODUCTION
            </div>
            <h2 className="display-medium" style={{ marginBottom: '30px' }}>
              Bridging the Gap in <span style={{ color: '#ff6b00' }}>Sponsorship</span>
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', lineHeight: '1.9', marginBottom: '24px' }}>
              In today's experience-driven market, brands are looking beyond conventional advertising and seeking deeper, more meaningful engagement with their audiences through events, on-ground activations, and community-focused initiatives.
            </p>
            <p className="body-large" style={{ color: 'var(--text-secondary)', lineHeight: '1.9', marginBottom: '24px' }}>
              Recognizing this shift, Zenthara has created a dedicated vertical – <strong style={{ color: 'var(--text-primary)' }}>Zenthara Sponsorship & Brand Alliances</strong> – to bridge the gap between organizations that need sponsorship support and brands that are ready to invest in high-impact visibility.
            </p>
            <div style={{
              background: 'linear-gradient(135deg, rgba(0, 74, 173, 0.1) 0%, rgba(0, 74, 173, 0.05) 100%)',
              border: '2px solid rgba(255, 107, 0, 0.3)',
              padding: '30px',
              marginTop: '30px'
            }}>
              <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8', fontStyle: 'italic' }}>
                This initiative is designed as a <strong style={{ color: '#ff6b00' }}>first-of-its-kind sponsorship desk</strong> within a retail-focused consulting and leasing platform, leveraging Zenthara's strong relationships with malls, developers, brands, corporates, and event organizers across multiple cities. Our role is to become the strategic link that aligns the right sponsor with the right event, at the right time, with clearly defined outcomes.
              </p>
            </div>
          </div>
        </section>

        {/* Vision Section */}
        <section style={{ 
          background: 'linear-gradient(135deg, #001a3d 0%, #002b5c 100%)', 
          padding: '100px 7.6923%' 
        }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '60px' }}>
              <h2 style={{ 
                fontSize: '42px', 
                fontWeight: '700', 
                color: '#ffffff',
                marginBottom: '20px'
              }}>
                Vision & <span style={{ color: '#ff6b00' }}>Purpose</span>
              </h2>
              <p style={{ fontSize: '18px', color: 'rgba(255,255,255,0.8)', maxWidth: '800px', margin: '0 auto', lineHeight: '1.8' }}>
                To build a sustainable, outcome-driven sponsorship ecosystem where every association creates value for all parties involved – the organizer, the sponsor, and the consumer.
              </p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '30px' }} className="vision-grid">
              <div style={{
                padding: '40px 30px',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 107, 0, 0.3)',
                textAlign: 'center'
              }}>
                <div style={{ 
                  width: '80px', 
                  height: '80px', 
                  background: 'rgba(255, 107, 0, 0.2)', 
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px',
                  color: '#ff6b00'
                }}>
                  <Users size={40} />
                </div>
                <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#ffffff', marginBottom: '12px' }}>
                  Enable Organizations
                </h3>
                <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)', lineHeight: '1.6' }}>
                  Enable organizations and event IPs to access structured sponsorship support
                </p>
              </div>

              <div style={{
                padding: '40px 30px',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 107, 0, 0.3)',
                textAlign: 'center'
              }}>
                <div style={{ 
                  width: '80px', 
                  height: '80px', 
                  background: 'rgba(255, 107, 0, 0.2)', 
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px',
                  color: '#ff6b00'
                }}>
                  <Target size={40} />
                </div>
                <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#ffffff', marginBottom: '12px' }}>
                  Help Brands Invest
                </h3>
                <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)', lineHeight: '1.6' }}>
                  Help brands invest their sponsorship budgets more intelligently and effectively
                </p>
              </div>

              <div style={{
                padding: '40px 30px',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 107, 0, 0.3)',
                textAlign: 'center'
              }}>
                <div style={{ 
                  width: '80px', 
                  height: '80px', 
                  background: 'rgba(255, 107, 0, 0.2)', 
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px',
                  color: '#ff6b00'
                }}>
                  <Building2 size={40} />
                </div>
                <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#ffffff', marginBottom: '12px' }}>
                  Seamless Integration
                </h3>
                <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)', lineHeight: '1.6' }}>
                  Integrate sponsorships seamlessly into malls, high streets, mixed-use developments, and community spaces
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Who We Serve Section */}
        <section style={{ background: 'var(--bg-primary)', padding: '100px 7.6923%' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '60px' }}>
              <div style={{
                display: 'inline-block',
                padding: '8px 16px',
                background: 'rgba(255, 107, 0, 0.1)',
                border: '1px solid #ff6b00',
                marginBottom: '20px',
                fontSize: '14px',
                color: '#ff6b00',
                fontWeight: '500'
              }}>
                WHO WE SERVE
              </div>
              <h2 className="display-medium" style={{ marginBottom: '20px' }}>
                Our <span style={{ color: '#ff6b00' }}>Stakeholders</span>
              </h2>
              <p className="body-medium" style={{ color: 'var(--text-muted)', maxWidth: '700px', margin: '0 auto' }}>
                We work with a broad but clearly defined set of stakeholders
              </p>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: '30px'
            }} className="stakeholders-grid">
              {stakeholders.map((item, index) => (
                <div key={index} style={{
                  padding: '40px 25px',
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid var(--border-subtle)',
                  textAlign: 'center',
                  transition: 'all 0.3s ease-in-out'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#ff6b00';
                  e.currentTarget.style.transform = 'translateY(-5px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--border-subtle)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
                >
                  <div style={{ color: '#ff6b00', marginBottom: '20px' }}>
                    {item.icon}
                  </div>
                  <h3 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '12px' }}>
                    {item.title}
                  </h3>
                  <p style={{ fontSize: '14px', color: 'var(--text-muted)', lineHeight: '1.6' }}>
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Key Offerings Section */}
        <section style={{ background: 'var(--bg-primary)', padding: '100px 7.6923%' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '60px' }}>
              <div style={{
                display: 'inline-block',
                padding: '8px 16px',
                background: 'rgba(255, 107, 0, 0.1)',
                border: '1px solid #ff6b00',
                marginBottom: '20px',
                fontSize: '14px',
                color: '#ff6b00',
                fontWeight: '500'
              }}>
                KEY OFFERINGS
              </div>
              <h2 className="display-medium" style={{ marginBottom: '20px' }}>
                What We <span style={{ color: '#ff6b00' }}>Do</span>
              </h2>
              <p className="body-medium" style={{ color: 'var(--text-muted)', maxWidth: '800px', margin: '0 auto' }}>
                End-to-end support across the sponsorship lifecycle
              </p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {offerings.map((offering, index) => (
                <div key={index} style={{
                  padding: '40px',
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid var(--border-subtle)',
                  display: 'grid',
                  gridTemplateColumns: '1fr 2fr',
                  gap: '40px',
                  alignItems: 'start',
                  transition: 'all 0.3s ease-in-out'
                }}
                className="offering-card"
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#ff6b00';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--border-subtle)';
                }}
                >
                  <div>
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '50px',
                      height: '50px',
                      background: 'linear-gradient(135deg, #004aad 0%, #0066cc 100%)',
                      marginBottom: '16px',
                      color: '#ffffff',
                      fontSize: '20px',
                      fontWeight: '700'
                    }}>
                      {index + 1}
                    </div>
                    <h3 className="heading-2" style={{ color: '#ff6b00', marginBottom: '12px' }}>
                      {offering.title}
                    </h3>
                    <p className="body-medium" style={{ color: 'var(--text-muted)' }}>
                      {offering.description}
                    </p>
                  </div>
                  <div>
                    <ul style={{ listStyle: 'none', padding: 0 }}>
                      {offering.features.map((feature, idx) => (
                        <li key={idx} style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '12px',
                          marginBottom: '16px'
                        }}>
                          <CheckCircle2 size={20} color="#ff6b00" style={{ flexShrink: 0, marginTop: '2px' }} />
                          <span className="body-medium" style={{ lineHeight: '1.6' }}>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Unique Advantage Section */}
        <section style={{ 
          background: 'linear-gradient(135deg, #001a3d 0%, #002b5c 100%)', 
          padding: '100px 7.6923%' 
        }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '60px' }}>
              <h2 style={{ 
                fontSize: '42px', 
                fontWeight: '700', 
                color: '#ffffff',
                marginBottom: '20px'
              }}>
                Our Unique <span style={{ color: '#ff6b00' }}>Advantage</span>
              </h2>
              <p style={{ fontSize: '18px', color: 'rgba(255,255,255,0.8)', maxWidth: '800px', margin: '0 auto', lineHeight: '1.8' }}>
                What makes this initiative unique is that it is anchored within Zenthara's existing retail and real estate consulting framework
              </p>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: '30px'
            }} className="advantage-grid">
              {advantages.map((item, index) => (
                <div key={index} style={{
                  padding: '40px 25px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 107, 0, 0.3)',
                  textAlign: 'center',
                  transition: 'all 0.3s ease-in-out'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#ff6b00';
                  e.currentTarget.style.background = 'rgba(255, 107, 0, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(255, 107, 0, 0.3)';
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                }}
                >
                  <div style={{ color: '#ff6b00', marginBottom: '20px' }}>
                    {item.icon}
                  </div>
                  <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#ffffff', marginBottom: '12px' }}>
                    {item.title}
                  </h3>
                  <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)', lineHeight: '1.6' }}>
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Engagement & Commercials Section */}
        <section style={{ background: 'var(--bg-primary)', padding: '100px 7.6923%' }}>
          <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '40px' }}>
              <div style={{
                display: 'inline-block',
                padding: '8px 16px',
                background: 'rgba(255, 107, 0, 0.1)',
                border: '1px solid #ff6b00',
                marginBottom: '20px',
                fontSize: '14px',
                color: '#ff6b00',
                fontWeight: '500'
              }}>
                ENGAGEMENT & COMMERCIALS
              </div>
              <h2 className="display-medium" style={{ marginBottom: '20px' }}>
                Transparent <span style={{ color: '#ff6b00' }}>Partnership</span>
              </h2>
            </div>

            <div style={{
              background: 'rgba(0, 74, 173, 0.1)',
              border: '2px solid rgba(0, 74, 173, 0.3)',
              padding: '40px'
            }}>
              <p className="body-large" style={{ color: 'var(--text-secondary)', lineHeight: '1.9', marginBottom: '24px' }}>
                Each sponsorship engagement is unique in terms of scale, stakeholders, and outcomes. To ensure fairness and flexibility, commercials for our services are discussed and finalized at the time of negotiation and closure, on a mutually agreed percentage or structure that works for all parties.
              </p>
              <p className="body-large" style={{ color: 'var(--text-secondary)', lineHeight: '1.9' }}>
                Our approach is <strong style={{ color: '#ff6b00' }}>transparent and partnership-driven</strong> – we aim to create a win-win framework where organizers, sponsors, and Zenthara all see clear value from the association.
              </p>
            </div>
          </div>
        </section>

        {/* How to Get Started / Contact Section */}
        <section id="contact" style={{ background: 'var(--bg-primary)', padding: '100px 7.6923%' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '60px',
              alignItems: 'start'
            }} className="contact-grid">
              {/* How to Get Started */}
              <div>
                <div style={{
                  display: 'inline-block',
                  padding: '8px 16px',
                  background: 'rgba(255, 107, 0, 0.1)',
                  border: '1px solid #ff6b00',
                  marginBottom: '20px',
                  fontSize: '14px',
                  color: '#ff6b00',
                  fontWeight: '500'
                }}>
                  HOW TO GET STARTED
                </div>
                <h2 className="display-medium" style={{ marginBottom: '30px' }}>
                  Let's <span style={{ color: '#ff6b00' }}>Connect</span>
                </h2>
                <p className="body-medium" style={{ marginBottom: '30px', color: 'var(--text-muted)', lineHeight: '1.8' }}>
                  If you are:
                </p>
                <ul style={{ listStyle: 'none', padding: 0, marginBottom: '40px' }}>
                  <li style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '16px' }}>
                    <CheckCircle2 size={20} color="#ff6b00" style={{ flexShrink: 0, marginTop: '4px' }} />
                    <span className="body-medium">An organization or event IP owner seeking sponsors for your upcoming initiative</span>
                  </li>
                  <li style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    <CheckCircle2 size={20} color="#ff6b00" style={{ flexShrink: 0, marginTop: '4px' }} />
                    <span className="body-medium">A brand looking for relevant, focused sponsorship opportunities in retail and community environments</span>
                  </li>
                </ul>
                <p className="body-medium" style={{ marginBottom: '40px', color: 'var(--text-secondary)', lineHeight: '1.8' }}>
                  Connect with the Zenthara Sponsorship & Brand Alliances team to discuss your requirements. We will understand your objectives, evaluate the opportunity, and propose a structured way forward that aligns with your timelines and goals.
                </p>

                <div style={{ marginBottom: '30px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
                    <div style={{
                      width: '50px',
                      height: '50px',
                      background: 'linear-gradient(135deg, #004aad 0%, #0066cc 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Mail size={24} color="#ffffff" />
                    </div>
                    <div>
                      <p style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '4px' }}>Email Us</p>
                      <a href="mailto:sponsorship@vdmbi.org" style={{ fontSize: '16px', color: '#ff6b00', textDecoration: 'none', display: 'block' }}>
                        sponsorship@vdmbi.org
                      </a>
                      <a href="mailto:sales@vdmbi.org" style={{ fontSize: '14px', color: 'var(--text-secondary)', textDecoration: 'none', display: 'block', marginTop: '4px' }}>
                        sales@vdmbi.org
                      </a>
                      <a href="mailto:sandeep@vdmbi.org" style={{ fontSize: '14px', color: 'var(--text-secondary)', textDecoration: 'none', display: 'block', marginTop: '4px' }}>
                        sandeep@vdmbi.org
                      </a>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{
                      width: '50px',
                      height: '50px',
                      background: 'linear-gradient(135deg, #004aad 0%, #0066cc 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Phone size={24} color="#ffffff" />
                    </div>
                    <div>
                      <p style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '4px' }}>Call Us</p>
                      <a href="tel:+918050518788" style={{ fontSize: '16px', color: '#ff6b00', textDecoration: 'none' }}>
                        +91 8050518788
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div style={{
                padding: '50px',
                background: 'rgba(0, 74, 173, 0.05)',
                border: '2px solid rgba(0, 74, 173, 0.3)'
              }}>
                <h3 className="heading-1" style={{ marginBottom: '30px' }}>
                  Get in Touch
                </h3>
                <form>
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: 'var(--text-muted)' }}>
                      Your Name *
                    </label>
                    <input 
                      type="text" 
                      placeholder="Enter your name"
                      style={{
                        width: '100%',
                        padding: '14px 16px',
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid var(--border-subtle)',
                        color: 'var(--text-primary)',
                        fontSize: '16px',
                        outline: 'none'
                      }}
                    />
                  </div>
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: 'var(--text-muted)' }}>
                      Organization/Brand Name *
                    </label>
                    <input 
                      type="text" 
                      placeholder="Enter organization name"
                      style={{
                        width: '100%',
                        padding: '14px 16px',
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid var(--border-subtle)',
                        color: 'var(--text-primary)',
                        fontSize: '16px',
                        outline: 'none'
                      }}
                    />
                  </div>
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: 'var(--text-muted)' }}>
                      Email Address *
                    </label>
                    <input 
                      type="email" 
                      placeholder="Enter your email"
                      style={{
                        width: '100%',
                        padding: '14px 16px',
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid var(--border-subtle)',
                        color: 'var(--text-primary)',
                        fontSize: '16px',
                        outline: 'none'
                      }}
                    />
                  </div>
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: 'var(--text-muted)' }}>
                      I am a...
                    </label>
                    <select
                      style={{
                        width: '100%',
                        padding: '14px 16px',
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid var(--border-subtle)',
                        color: 'var(--text-primary)',
                        fontSize: '16px',
                        outline: 'none'
                      }}
                    >
                      <option value="">Select your role</option>
                      <option value="organizer">Event/IP Organizer seeking sponsors</option>
                      <option value="brand">Brand looking for sponsorship opportunities</option>
                      <option value="mall">Mall/Retail development</option>
                      <option value="corporate">Corporate organization</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: 'var(--text-muted)' }}>
                      Tell us about your requirement *
                    </label>
                    <textarea 
                      placeholder="Describe your sponsorship needs, event details, or brand objectives..."
                      rows={5}
                      style={{
                        width: '100%',
                        padding: '14px 16px',
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid var(--border-subtle)',
                        color: 'var(--text-primary)',
                        fontSize: '16px',
                        outline: 'none',
                        resize: 'vertical'
                      }}
                    />
                  </div>
                  <button 
                    type="submit"
                    style={{
                      width: '100%',
                      padding: '16px 32px',
                      background: '#ff6b00',
                      color: '#ffffff',
                      fontWeight: '600',
                      fontSize: '16px',
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'all 0.3s',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '10px'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#e55a00'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#ff6b00'}
                  >
                    Submit Inquiry
                    <ArrowRight size={20} />
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section style={{ 
          background: 'linear-gradient(135deg, #ff6b00 0%, #e55a00 100%)', 
          padding: '80px 7.6923%' 
        }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto', textAlign: 'center' }}>
            <h2 style={{ 
              fontSize: '36px', 
              fontWeight: '700', 
              color: '#ffffff',
              marginBottom: '20px'
            }}>
              Ready to Create Impactful Sponsorships?
            </h2>
            <p style={{ 
              fontSize: '18px', 
              color: 'rgba(255,255,255,0.9)',
              marginBottom: '40px',
              maxWidth: '700px',
              margin: '0 auto 40px'
            }}>
              Let Zenthara Sponsorship & Brand Alliances be your strategic partner in connecting the right brands with the right opportunities.
            </p>
            <Link to="/zenthara" style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '10px',
              padding: '16px 40px',
              background: '#004aad',
              color: '#ffffff',
              textDecoration: 'none',
              fontWeight: '600',
              fontSize: '16px',
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.3s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#003380'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#004aad'}
            >
              Explore Zenthara
              <ArrowRight size={20} />
            </Link>
          </div>
        </section>
      </main>
      <Footer />

      <style>{`
        @media (max-width: 1024px) {
          .stakeholders-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .advantage-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .offering-card {
            grid-template-columns: 1fr !important;
          }
        }
        @media (max-width: 768px) {
          .vision-grid {
            grid-template-columns: 1fr !important;
          }
          .stakeholders-grid {
            grid-template-columns: 1fr !important;
          }
          .advantage-grid {
            grid-template-columns: 1fr !important;
          }
          .contact-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ZentharaSponsorshipDetail;
